/**
 * DemoIntro — consistent explanatory opener for CSK demos.
 *
 * The opener is the first thing a reader sees on every demo, so it carries
 * three jobs at once: announce the theorem (memorable), name the forbidden
 * shape neutralized (explanatory), and frame what they will manipulate on
 * screen (beautiful). The visual treatment uses the theorem accent color to
 * tint a left rail and a soft background wash, so each demo arrives with its
 * own structural identity instead of a uniform gray card.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { DemoTheorem } from "../types.js";
import {
  FONT,
  GRAY,
  PRIMARY,
  THEOREM_BG,
  THEOREM_COLOR,
  type CanonicalTheoremName,
} from "../design-system/index.js";
import { SafeLink } from "./SafeLink.js";

export interface DemoIntroProps {
  readonly title: string;
  readonly whatYouSee: string;
  readonly whyItsSafe: string;
  readonly theorem?: DemoTheorem | readonly DemoTheorem[] | string;
  readonly agdaSource?: string;
  /**
   * Memorable one-line punchline summarizing the structural guarantee, e.g.
   * "Data and code live in disjoint cells — injection has no morphism." If
   * provided, it renders as a highlighted callout above the prose paragraphs.
   */
  readonly keyInsight?: string;
  /**
   * Name of the forbidden shape this demo rules out. Rendered as a small
   * eyebrow row, e.g. "Forbidden shape: DataValue → Instruction".
   */
  readonly structuralDefect?: string;
  /**
   * Concrete attack family or CWE/OWASP code the theorem neutralizes, e.g.
   * "CWE-89 · OWASP A03". Rendered next to the structural-defect eyebrow.
   */
  readonly attackFamily?: string;
}

export interface AgdaSourceLinkProps {
  readonly module: string;
  readonly repoUrl?: string;
}

export interface TheoremChipProps {
  readonly code: string;
  readonly color?: string;
}

function isTheoremList(theorem: DemoIntroProps["theorem"]): theorem is readonly DemoTheorem[] {
  return Array.isArray(theorem);
}

function isCanonicalTheoremName(name: string): name is CanonicalTheoremName {
  return Object.hasOwn(THEOREM_COLOR, name);
}

function theoremAccent(name: string): string {
  return isCanonicalTheoremName(name) ? THEOREM_COLOR[name] : PRIMARY[600];
}

function theoremWash(name: string | undefined): string {
  if (name !== undefined && isCanonicalTheoremName(name)) return THEOREM_BG[name];
  return "rgba(59,95,224,0.06)";
}

function firstTheoremName(theorem: DemoIntroProps["theorem"]): string | undefined {
  if (theorem === undefined) return undefined;
  if (typeof theorem === "string") return theorem;
  if (isTheoremList(theorem)) return theorem[0]?.name;
  return theorem.name;
}

function theoremLabel(theorem: DemoIntroProps["theorem"]): string | undefined {
  if (theorem === undefined) return undefined;
  if (typeof theorem === "string") return theorem;
  if (isTheoremList(theorem)) return theorem.map((entry) => entry.name).join(" + ");
  return theorem.name;
}

function theoremColor(theorem: DemoIntroProps["theorem"]): string {
  const first = firstTheoremName(theorem);
  return first === undefined ? PRIMARY[600] : theoremAccent(first);
}

export function TheoremChip({ code, color = PRIMARY[700] }: TheoremChipProps): React.JSX.Element {
  return (
    <span
      style={{
        background: `${color}14`,
        border: `1px solid ${color}55`,
        borderRadius: 999,
        color,
        display: "inline-flex",
        fontFamily: FONT.hyperlegible,
        fontSize: "0.75rem",
        fontWeight: 800,
        lineHeight: 1,
        padding: "0.35rem 0.55rem",
        whiteSpace: "nowrap",
      }}
    >
      {code}
    </span>
  );
}

export function AgdaSourceLink({
  module,
  repoUrl = "https://github.com/InvariantHoldings/categorical-safety-kernel/blob/main",
}: AgdaSourceLinkProps): React.JSX.Element {
  const path = module.startsWith("agda/") ? module : `agda/${module}`;
  return (
    <SafeLink
      href={`${repoUrl}/${path}`}
      style={{
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 999,
        color: GRAY[700],
        display: "inline-flex",
        fontFamily: FONT.hyperlegible,
        fontSize: "0.72rem",
        fontWeight: 700,
        padding: "0.35rem 0.55rem",
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
    >
      Proof reference: {path}
    </SafeLink>
  );
}

interface MetaTagProps {
  readonly label: string;
  readonly value: string;
  readonly accent: string;
}

function MetaTag({ label, value, accent }: MetaTagProps): React.JSX.Element {
  return (
    <span
      style={{
        alignItems: "baseline",
        background: GRAY[50],
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 6,
        color: GRAY[700],
        display: "inline-flex",
        flexWrap: "wrap",
        fontFamily: FONT.hyperlegible,
        fontSize: "0.7rem",
        gap: "0.4rem",
        maxWidth: "100%",
        minWidth: 0,
        padding: "0.25rem 0.5rem",
      }}
    >
      <span
        style={{
          color: accent,
          fontWeight: 800,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span style={{ color: GRAY[700], overflowWrap: "anywhere" }}>{value}</span>
    </span>
  );
}

export function DemoIntro({
  title,
  whatYouSee,
  whyItsSafe,
  theorem,
  agdaSource,
  keyInsight,
  structuralDefect,
  attackFamily,
}: DemoIntroProps): React.JSX.Element {
  const label = theoremLabel(theorem);
  const accent = theoremColor(theorem);
  const wash = theoremWash(firstTheoremName(theorem));
  const hasMeta = structuralDefect !== undefined || attackFamily !== undefined;

  return (
    <section
      aria-label={`${title} introduction`}
      style={{
        background: `linear-gradient(135deg, ${wash} 0%, white 55%)`,
        border: `1px solid ${GRAY[300]}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: 10,
        boxShadow: `0 1px 2px rgba(15, 23, 42, 0.06), inset 0 0 0 1px ${accent}10`,
        marginBottom: "1rem",
        overflow: "hidden",
        padding: "1rem 1.1rem",
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          background: `linear-gradient(90deg, ${accent} 0%, ${accent}00 70%)`,
          height: 2,
          left: 0,
          opacity: 0.55,
          position: "absolute",
          right: 0,
          top: 0,
        }}
      />
      <div
        style={{
          alignItems: "flex-start",
          display: "flex",
          flexWrap: "wrap",
          gap: "0.75rem",
          justifyContent: "space-between",
          marginBottom: "0.6rem",
        }}
      >
        <h1
          style={{
            color: GRAY[900],
            fontFamily: FONT.heading,
            fontSize: "1.3rem",
            fontWeight: 800,
            letterSpacing: "-0.01em",
            margin: 0,
          }}
        >
          {title}
        </h1>
        {label !== undefined && <TheoremChip code={label} color={accent} />}
      </div>

      {hasMeta && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.4rem",
            marginBottom: "0.75rem",
          }}
        >
          {structuralDefect !== undefined && (
            <MetaTag label="Forbidden shape" value={structuralDefect} accent={accent} />
          )}
          {attackFamily !== undefined && (
            <MetaTag label="Neutralizes" value={attackFamily} accent={accent} />
          )}
        </div>
      )}

      {keyInsight !== undefined && (
        <div
          style={{
            alignItems: "flex-start",
            background: `${accent}10`,
            border: `1px solid ${accent}40`,
            borderRadius: 8,
            color: GRAY[900],
            display: "flex",
            fontSize: "0.92rem",
            fontStyle: "italic",
            fontWeight: 500,
            gap: "0.6rem",
            lineHeight: 1.45,
            margin: "0 0 0.85rem",
            padding: "0.65rem 0.8rem",
          }}
        >
          <span
            aria-hidden
            style={{
              color: accent,
              flexShrink: 0,
              fontFamily: FONT.mono,
              fontSize: "1rem",
              fontStyle: "normal",
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            ❝
          </span>
          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{keyInsight}</span>
        </div>
      )}

      <div style={{ display: "grid", gap: "0.6rem" }}>
        <p style={{ color: GRAY[700], fontSize: "0.875rem", lineHeight: 1.55, margin: 0 }}>
          <strong style={{ color: GRAY[900] }}>What you see: </strong>
          {whatYouSee}
        </p>
        <p style={{ color: GRAY[700], fontSize: "0.875rem", lineHeight: 1.55, margin: 0 }}>
          <strong style={{ color: GRAY[900] }}>Why it is safe by construction: </strong>
          {whyItsSafe}
        </p>
        {agdaSource !== undefined && (
          <div style={{ marginTop: "0.15rem" }}>
            <AgdaSourceLink module={agdaSource} />
          </div>
        )}
      </div>
    </section>
  );
}
